import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { auth } from '../firebase';
import styles from './FeaturesAndPricingPage.module.css'; // Import as a module object

function PricingCard({ plan, price, features, isPopular }) {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const functions = getFunctions();

  // Handler for the Pro plan using Firebase callable functions
  const handleSelectPlan = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      // Create a reference to your callable function.
      const createStripeSession = httpsCallable(functions, "createStripeSession");
      // Call the function with a top-level object containing plan and userId.
      const result = await createStripeSession({ plan: "Pro", userId: user.uid });
      const sessionId = result.data.id;
      console.log("Received session ID:", sessionId);

      // Load Stripe.js using your publishable key.
      const stripe = await loadStripe(
        "pk_test_51Qzl4xB2xpuvoM49SPm8MOswuGnaNaQ2u8w8S6bKEukqjwH6USFNE070jjnCZq6W5QfnvHW7qcU0RLV0wkrbo1B800d4pkrwcl"
      );
      if (!stripe) {
        alert("Failed to load Stripe. Please refresh the page and try again.");
        return;
      }

      // Redirect to Stripe Checkout.
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        console.error("Stripe redirection error:", error);
        alert("Payment processing failed. Please try again.");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      alert("Payment processing failed. Please try again.");
    }
  };

  return (
    <div className={`${styles['pricing-card']} ${isPopular ? styles['popular'] : ''}`}>
      {isPopular && <div className={styles['popular-badge']}>Most Popular</div>}
      <h3 className={styles['pricing-plan']}>{plan}</h3>
      <p className={styles['pricing-price']}>{price}</p>
      <ul className={styles['pricing-features']}>
        {features.map((feature, index) => (
          <li key={index} className={styles['pricing-feature']}>
            <span className={styles['feature-icon']}>✓</span> {feature}
          </li>
        ))}
      </ul>
      {plan === "Pro" ? (
        <button className={styles['button-primary']} onClick={handleSelectPlan}>
          Upgrade Now
        </button>
      ) : plan === "Basic" ? (
        <Link to="/signup">
          <button className={styles['button-secondary']}>Get Started</button>
        </Link>
      ) : (
        <Link to="/contact">
          <button className={styles['button-outline']}>Contact Sales</button>
        </Link>
      )}
    </div>
  );
}

const FeaturesAndPricingPage = () => {
  // Pricing plan features
  const basicFeatures = [
    "1 team with up to 15 players",
    "Basic announcements (text only)",
    "Simple player roster management",
    "Basic game scheduling",
    "Final score tracking",
    "100MB photo storage",
    "Email support (48-hour response)"
  ];

  const proFeatures = [
    "Up to 5 teams with 100 players each",
    "Enhanced announcements with media",
    "Advanced player profiles with stats",
    "Comprehensive scheduling with notifications",
    "Detailed availability tracking",
    "Team leaders and performance tracking",
    "5GB media storage for photos and videos",
    "Priority support (24-hour response)"
  ];

  const enterpriseFeatures = [
    "Unlimited teams and players",
    "Organization-wide announcements",
    "Cross-team player database",
    "Multi-team scheduling with conflict detection",
    "Organization-wide availability tracking",
    "Advanced analytics across all teams",
    "Unlimited media storage",
    "Dedicated account manager"
  ];

  return (
    <div className={styles['features-page']}>
      <header className={styles['page-header']}>
        <h1>Leading Features</h1>
        <p>
          Choose the plan that fits your team's needs and growth trajectory
        </p>
      </header>

      {/* Feature highlight section */}
      <section className={styles['feature-highlights']}>
        <div className={styles['feature-card']}>
          <div className={styles['feature-icon']}>📋</div>
          <h3>Team Management</h3>
          <p>Manage your roster, track performance, and organize your team efficiently.</p>
        </div>
        <div className={styles['feature-card']}>
          <div className={styles['feature-icon']}>📅</div>
          <h3>Scheduling</h3>
          <p>Create games, practices, and track player availability with ease.</p>
        </div>
        <div className={styles['feature-card']}>
          <div className={styles['feature-icon']}>📊</div>
          <h3>Statistics</h3>
          <p>Track individual and team performance with detailed statistics.</p>
        </div>
      </section>

      {/* Pricing section */}
      <h2 className={styles['section-title']}>Pricing Plans</h2>
      <div className={styles['pricing-container']}>
        <PricingCard
          plan="Basic"
          price="Free"
          features={basicFeatures}
          isPopular={false}
        />
        <PricingCard
          plan="Pro"
          price="$9.99/month"
          features={proFeatures}
          isPopular={true}
        />
        <PricingCard
          plan="Enterprise"
          price="Contact Us"
          features={enterpriseFeatures}
          isPopular={false}
        />
      </div>

      <section className={styles['compare-plans']}>
        <h2>Why Upgrade to Pro?</h2>
        <p>
          Take your team management to the next level with advanced features designed for competitive teams.
          The Pro plan gives you everything you need to manage multiple teams effectively, track detailed 
          performance stats, and keep everyone on the same page with enhanced communication tools.
        </p>
        <div className={styles['upgrade-benefits']}>
          <div className={styles['benefit']}>
            <div className={styles['benefit-icon']}>📊</div>
            <h3>Enhanced Performance Tracking</h3>
            <p>Track team leaders and detailed player statistics to identify strengths and areas for improvement.</p>
          </div>
          <div className={styles['benefit']}>
            <div className={styles['benefit-icon']}>📅</div>
            <h3>Advanced Availability</h3>
            <p>Seamlessly manage player availability with calendar integration and automatic notifications.</p>
          </div>
          <div className={styles['benefit']}>
            <div className={styles['benefit-icon']}>👥</div>
            <h3>Manage More Teams</h3>
            <p>Scale up to 5 teams with 100 players each, perfect for clubs managing multiple age groups or divisions.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesAndPricingPage;